import { Checkbox } from "@volca/ui/components/ui/checkbox";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { dayjs } from "@/core/utils/date";
import { ApprovalMark } from "@/payroll/approvals/approval-mark";
import type {
  IResolvedSheetApprovalUnit,
  ISheetApprovalRecipient,
} from "./types";
import { getRecipientUnits } from "./utils";

interface RequestApprovalRecipientRowProps {
  recipient: ISheetApprovalRecipient;
  units: IResolvedSheetApprovalUnit[];
  showStatus: boolean;
  checked: boolean;
  onToggle: (userId: string) => void;
}

export const RequestApprovalRecipientRow = ({
  recipient,
  units,
  showStatus,
  checked,
  onToggle,
}: RequestApprovalRecipientRowProps) => {
  const ownedUnits = getRecipientUnits(recipient, units);
  const emailedAt = recipient.latest_delivery?.sent_at;
  const checkboxId = `approval-recipient-${recipient.user_id}`;

  return (
    <Stack
      direction="row"
      align="start"
      gap={2}
      className="w-full px-4 py-3 transition-colors hover:bg-neutral-subtle-hovered"
    >
      <Checkbox
        id={checkboxId}
        checked={checked}
        onCheckedChange={() => onToggle(recipient.user_id)}
        className="mt-0.5 shrink-0"
      />
      <label htmlFor={checkboxId} className="min-w-0 flex-1 cursor-pointer">
        <Stack className="w-full" align="start" gap={1}>
          <Stack className="w-full" direction="row" align="baseline" gap={1}>
            <Text variant="caption" className="shrink-0 font-semibold">
              {recipient.name}
            </Text>
            <Text
              variant="caption"
              tone="muted"
              className="min-w-0 truncate"
              title={recipient.email ?? undefined}
            >
              {recipient.email}
            </Text>
          </Stack>

          <Stack
            className="w-full"
            direction="row"
            align="baseline"
            justify="between"
            gap={2}
          >
            <Stack
              direction="row"
              align="center"
              gap={4}
              className="min-w-0 flex-wrap"
            >
              {ownedUnits.map((unit) => (
                <Stack
                  key={unit.access_key}
                  direction="row"
                  align="center"
                  gap={1}
                >
                  {showStatus && <ApprovalMark accessKey={unit} withTooltip />}
                  <Text
                    variant="caption"
                    tone="muted"
                    title={unit.sheet_names.join(", ")}
                  >
                    {unit.name} · {unit.sheet_names.length}{" "}
                    {unit.sheet_names.length === 1 ? "sheet" : "sheets"}
                  </Text>
                </Stack>
              ))}
            </Stack>

            {emailedAt && (
              <Text
                variant="caption"
                tone="muted"
                className="shrink-0 whitespace-nowrap"
              >
                Emailed {dayjs(emailedAt).fromNow()}
              </Text>
            )}
          </Stack>
        </Stack>
      </label>
    </Stack>
  );
};
